import React, { useState } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Collapse, Box, Typography, Divider, useMediaQuery, useTheme } from '@mui/material';
import { Dashboard, Contacts, ShoppingCart, Receipt, Payment, BarChart, ExpandLess, ExpandMore, Description, NoteAdd, CreditCard, Campaign, CloudDownload } from '@mui/icons-material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ReceiptIcon from '@mui/icons-material/Receipt'; // ✅ Import Receipt icon for Ticket
import LocalShippingIcon from '@mui/icons-material/LocalShipping'; // ✅ Import icon for Delivery Notes
import StorefrontIcon from '@mui/icons-material/Storefront'; // Add this import
import InventoryIcon from '@mui/icons-material/Inventory';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useModulePermissions } from '../hooks/useModulePermissions'; // ✅ Import permission hook

const drawerWidth = 240;

const SideNav = ({ mobileOpen, onMobileClose }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openContacts, setOpenContacts] = useState(false);
  
  // ✅ Get permission checking functions
  const { canViewModule, loading: permsLoading } = useModulePermissions();

  const handleContactsClick = () => {
    setOpenContacts(!openContacts);
  };

  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const menuItems = [
    { text: t("dashboard"), icon: <Dashboard />, path: '/dashboard', module: 'Dashboard' },
    { text: t("customers"), icon: <Contacts />, path: '/customers', module: 'Customers' }, 
    { text: t("suppliers"), icon: <Contacts />, path: '/suppliers', module: 'Suppliers' }, 
    { text: t("products"), icon: <ShoppingCart />, path: '/products', module: 'Products' },
    { text: t("shopify"), icon: <StorefrontIcon />, path: '/shopify/products', module: 'Shopify' },
    { text: t("wishlist"), icon: <FavoriteBorderIcon />, path: '/wishlist', module: 'Wishlist' },
    {
      text: t("sales"),
      icon: <Receipt />,
      subItems: [
        { text: t("offers"), icon: <Description />, path: '/sales/offers', module: 'Offers' },
        { text: t("invoices"), icon: <NoteAdd />, path: '/invoices', module: 'Invoices' },
        { text: t("delivery_notes"), icon: <LocalShippingIcon />, path: '/sales/delivery-notes', module: 'Delivery Notes' },
        { text: t("received_peppol_invoices") || 'Received Peppol Invoices', icon: <CloudDownload />, path: '/sales/received-peppol-invoices', module: 'ReceivedInvoices' },
        { text: t("credit_notes"), icon: <CreditCard />, path: '/sales/credit-notes', module: 'CreditNotes' },
        {
          text: t('tickets_de_caisse') || 'Tickets de Caisse',
          icon: <ReceiptIcon />,
          path: '/tickets',
          module: 'POS'
        },
      ],
    },
    { text: t("purchases"), icon: <ShoppingCart />, path: '/purchases', module: 'Purchases' },
    { text: t("payments"), icon: <Payment />, path: '/payments', module: 'Payments' },
    { text: t("reports"), icon: <BarChart />, path: '/reports', module: 'Reports' },
    { text: t("announcements"), icon: <Campaign />, path: '/announcements', module: 'Announcements' },
    {
      text: t("inventory"),
      icon: <InventoryIcon />,
      path: '/inventory',
      module: 'Inventory',
    },
  ];

  // ✅ Filter menu items based on permissions
  const getFilteredMenuItems = () => {
    return menuItems.filter((item) => {
      // If no module specified, always show (like announcements)
      if (!item.module && !item.subItems) {
        return true;
      }

      // For items with subitems, check if user has access to ANY subitem
      if (item.subItems) {
        const visibleSubItems = item.subItems.filter(
          (sub) => !sub.module || canViewModule(sub.module)
        );
        return visibleSubItems.length > 0;
      }

      // For regular items, check module permission
      return canViewModule(item.module);
    });
  };

  const filteredMenuItems = getFilteredMenuItems();

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const drawerContent = (
    <>
      {/* ✅ Logo/Brand Section - Fixed at top, no scrolling */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          gap: 1.5,
          height: { xs: 64, sm: 70 },
          minHeight: { xs: 64, sm: 70 },
          px: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/assets/logo.png"
          alt="Logo"
          sx={{
            width: 45,
            height: 45,
            objectFit: 'contain',
            borderRadius: '10px',
            backgroundColor: '#fff',
            padding: '5px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 700, 
              color: '#fff',
              fontSize: '0.95rem',
              lineHeight: 1.2,
            }}
          >
            Billeur
          </Typography>
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.65rem',
              lineHeight: 1.2,
            }}
          >
            Invoice Management
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

      {/* ✅ Scrollable menu area */}
      <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {permsLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Loading menu...
            </Typography>
          </Box>
        ) : (
          <List sx={{ px: 1, pt: 2 }}>
            {filteredMenuItems.map((item, index) => (
            <div key={index}>
              {item.subItems ? (
                <>
                  <ListItem 
                    button 
                    onClick={handleContactsClick}
                    sx={{
                      mb: 0.5,
                      borderRadius: '12px',
                      backgroundColor: item.subItems.some(sub => isActive(sub.path)) 
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'transparent',
                      background: item.subItems.some(sub => isActive(sub.path))
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'transparent',
                      color: item.subItems.some(sub => isActive(sub.path)) ? '#fff' : 'inherit',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: item.subItems.some(sub => isActive(sub.path))
                          ? 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)'
                          : 'rgba(102, 126, 234, 0.08)',
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: item.subItems.some(sub => isActive(sub.path)) ? '#fff' : '#667eea',
                      minWidth: 40,
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      sx={{
                        '& .MuiTypography-root': {
                          fontWeight: item.subItems.some(sub => isActive(sub.path)) ? 600 : 500,
                          fontSize: '0.95rem',
                          color: item.subItems.some(sub => isActive(sub.path)) ? '#fff' : '#2d3748',
                        }
                      }}
                    />
                    {openContacts ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={openContacts} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.subItems
                        .filter((sub) => !sub.module || canViewModule(sub.module))
                        .map((subItem, subIndex) => (
                        <ListItem 
                          button 
                          component={Link} 
                          to={subItem.path} 
                          key={subIndex}
                          onClick={handleNavClick}
                          sx={{ 
                            pl: 4,
                            mb: 0.5,
                            ml: 2,
                            borderRadius: '12px',
                            backgroundColor: isActive(subItem.path) 
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'transparent',
                            background: isActive(subItem.path)
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'transparent',
                            color: isActive(subItem.path) ? '#fff' : 'inherit',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: isActive(subItem.path)
                                ? 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)'
                                : 'rgba(102, 126, 234, 0.08)',
                              transform: 'translateX(4px)',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ 
                            color: isActive(subItem.path) ? '#fff' : '#667eea',
                            minWidth: 36,
                          }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={subItem.text}
                            sx={{
                              '& .MuiTypography-root': {
                                fontWeight: isActive(subItem.path) ? 600 : 500,
                                fontSize: '0.9rem',
                                color: isActive(subItem.path) ? '#fff' : '#4a5568',
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem 
                  button 
                  component={Link} 
                  to={item.path}
                  onClick={handleNavClick}
                  sx={{
                    mb: 0.5,
                    borderRadius: '12px',
                    backgroundColor: isActive(item.path) 
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'transparent',
                    background: isActive(item.path)
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'transparent',
                    color: isActive(item.path) ? '#fff' : 'inherit',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: isActive(item.path)
                        ? 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)'
                        : 'rgba(102, 126, 234, 0.08)',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ 
                    color: isActive(item.path) ? '#fff' : '#667eea',
                    minWidth: 40,
                  }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.text}
                    sx={{
                      '& .MuiTypography-root': {
                        fontWeight: isActive(item.path) ? 600 : 500,
                        fontSize: '0.95rem',
                        color: isActive(item.path) ? '#fff' : '#2d3748',
                      }
                    }}
                  />
                </ListItem>
              )}
            </div>
          ))}
          </List>
        )}
      </Box>
    </>
  
  );

  return (
    <Box component="nav">
      {/* Mobile Drawer */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
              borderRight: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        /* Desktop Drawer */
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              background: 'linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%)',
              borderRight: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}
    </Box>
  );
};

export default SideNav;